/**
 * @genesis_domain Authentication Context Provider (V7.4.8)
 * @genesis_intent Centralized authentication state management with TanStack Query for session persistence and event-driven cache updates. Provides login, logout, and registration functionality.
 * @genesis_lob State is kept close to the render cycle through React Context with TanStack Query for server state. Auth state flows through single source of truth with event-driven cache updates.
 * @genesis_interactions Consumes: @supabase/supabase-js, @tanstack/react-query, ../lib/supabase. Provides: AuthProvider component, useAuth hook, authentication state (currentUser, session, isInitializing).
 * @genesis_boundaries This context is FORBIDDEN from handling UI rendering logic, routing decisions, or business logic beyond auth state management.
 * @genesis_ai_trail Future agents: Uses TanStack Query for session management with event-driven cache updates. No Kill Switch anti-pattern. Auth listener uses useEffect with proper cleanup. No useEffect for data fetching.
 */

import type { Session, User } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { supabase } from '@/core/api/supabase';

interface RegisterResult {
  requiresVerification: boolean;
}

interface AuthContextType {
  currentUser: User | null;
  session: Session | null;
  isInitializing: boolean;
  isAuthenticated: boolean;
  isRecoveryFlow: boolean;
  setRecoveryFlow: (isRecovery: boolean) => void;
  clearRecoveryFlow: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<RegisterResult>;
  verifySignup: (email: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Custom hook to manage auth state with TanStack Query
function useSupabaseAuth() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Use TanStack Query for session management (NO useEffect, NO manual state syncing)
  const { data: session, isLoading } = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
    staleTime: Infinity, // Auth is event-driven; never refetch automatically
    gcTime: Infinity,
    retry: false, // Prevent infinite retries on initial auth failure
    refetchOnWindowFocus: false, // Prevent permission overlays from triggering background refetch storms
  });

  // Track forced password recovery flow
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);

  // Initialize Supabase auth listener once (event-driven cache updates)
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Event-driven cache update - no re-fetch, just update the cache
      queryClient.setQueryData(['auth', 'session'], session);

      // Intercept PASSWORD_RECOVERY event to force the recovery UI
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryFlow(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const currentUser = session?.user ?? null;
  const isInitializing = isLoading;

  const clearRecoveryFlow = () => setIsRecoveryFlow(false);

  return {
    currentUser,
    session: session ?? null,
    isInitializing,
    isRecoveryFlow,
    setIsRecoveryFlow,
    clearRecoveryFlow,
    error,
    setError,
    queryClient,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { currentUser, session, isInitializing, isRecoveryFlow, setIsRecoveryFlow, clearRecoveryFlow, error, setError, queryClient } = useSupabaseAuth();

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // CRITICAL FIX: Eliminate the race condition by injecting the session synchronously
      if (data.session) {
        queryClient.setQueryData(['auth', 'session'], data.session);
        // ENTERPRISE PREDICTIVE PREFETCHING: Fire and forget dashboard data
        // Prefetch workspaces to mask network latency
        try {
          const { workspaceKeys } = await import('@/core/api/queryKeys.js');
          queryClient.prefetchQuery({
            queryKey: workspaceKeys.lists(),
            queryFn: async () => {
              const { WorkspaceService } = await import('@/domains/workspaces/services/workspaceService.js');
              return WorkspaceService.getWorkspaces();
            },
          });
        } catch {
          // Silently fail - prefetching is optional optimization
          // console.debug removed per ESLint no-console rule
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    }
  };

  const register = async (email: string, password: string, name: string): Promise<RegisterResult> => {
    setError(null);
    try {
      // Use AuthService.register() which properly handles name metadata
      const { register: authServiceRegister } = await import('@/domains/auth/services/authService.js');
      const { session } = await authServiceRegister({ email, password, name });

      // If session is null, email confirmations are enabled — user must verify first
      if (!session) {
        return { requiresVerification: true };
      }

      // CRITICAL FIX: Eliminate the race condition by injecting the session synchronously
      queryClient.setQueryData(['auth', 'session'], session);
      // ENTERPRISE PREDICTIVE PREFETCHING: Fire and forget dashboard data
      // Prefetch workspaces to mask network latency
      try {
        const { workspaceKeys } = await import('@/core/api/queryKeys.js');
        queryClient.prefetchQuery({
          queryKey: workspaceKeys.lists(),
          queryFn: async () => {
            const { WorkspaceService } = await import('@/domains/workspaces/services/workspaceService.js');
            return WorkspaceService.getWorkspaces();
          },
        });
      } catch {
        // Silently fail - prefetching is optional optimization
        // console.debug removed per ESLint no-console rule
      }

      return { requiresVerification: false };
    } catch (err) {
      // Forensic logging: capture full error object to distinguish Supabase error codes
      console.error('Registration failed - full error:', JSON.stringify(err, Object.getOwnPropertyNames(err), 2));
      setError(err instanceof Error ? err.message : 'Registration failed');
      throw err;
    }
  };

  const logout = async () => {
    setError(null);

    try {
      // Use AuthService.logout() which properly clears query cache
      const { logout: authServiceLogout } = await import('@/domains/auth/services/authService.js');
      await authServiceLogout();
      // CRITICAL FIX: Forcefully clear session cache synchronously
      queryClient.setQueryData(['auth', 'session'], null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
      throw err;
    }
  };

  const verifySignup = async (email: string, token: string) => {
    setError(null);
    try {
      const { verifySignupOtp } = await import('@/domains/auth/services/authService.js');
      const data = await verifySignupOtp(email, token);
      // Inject the session synchronously to eliminate race conditions
      if (data.session) {
        queryClient.setQueryData(['auth', 'session'], data.session);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Email verification failed');
      throw err;
    }
  };

  const value: AuthContextType = {
    currentUser,
    session,
    isInitializing,
    isAuthenticated: !!currentUser,
    isRecoveryFlow,
    setRecoveryFlow: setIsRecoveryFlow,
    clearRecoveryFlow,
    login,
    register,
    verifySignup,
    logout,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}


